<template>
  <div v-if="workExperiencesPage">
    <div class="d-flex justify-content-between align-items-center w-100 mb-2">
      <h2 class="mb-0">{{ title }}</h2>
      <div class="d-flex align-items-center">
        <a-button
          v-if="allowAdd"
          type="primary"
          icon="plus"
          class="ml-2"
          @click="addWorkExperienceModalVisible = true"
        />
        <portal-target name="work-experiences-filter-buttons"></portal-target>
      </div>
    </div>

    <work-experiences-filter
      v-if="allowFiltering"
      :profile="profile"
      :filters="{ ...filtersRef, ...workExperiencesFilter }"
      :number-of-results="workExperiencesPagination?.total"
      :unchangeable-filter="workExperiencesFilter"
      @apply-filters="(newFilter) => applyFilters(newFilter)"
    />

    <div :class="allowFiltering ? 'card' : ''">
      <work-experiences-table
        :work-experiences="workExperiencesPage"
        :work-experiences-pagination="workExperiencesPagination"
        :page-size="pageSizeRef"
        :current-page="currentPage"
        :acl="acl"
        :profile="profile"
        :loading-table="loadingTable"
        :work-experiences-columns="workExperiencesColumns"
        :skills="skills"
        :roles="roles"
        @get-work-experiences-page="(params) => getWorkExperiencesPage(params)"
        @apply-sorting="(newSorting) => applySorting(newSorting)"
        @update-work-experience="
          (updatedWorkExperience) => handleUpdateWorkExperience(updatedWorkExperience)
        "
        @delete-work-experience="(workExperienceId) => handleDeleteWorkExperience(workExperienceId)"
      />
    </div>

    <!-- CREATE NEW WORK EXPERIENCE MODAL -->
    <add-work-experience-modal
      :visible="addWorkExperienceModalVisible"
      :profile="profile"
      :skills="skills"
      :roles="roles"
      @store-work-experience="handleStoreWorkExperience"
      @close="addWorkExperienceModalVisible = false"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, computed, getCurrentInstance } from "vue";
import { BaseAclInterface, ProfileInterface } from "@/modules/authentication/types";
import { PaginationInterface, TableColumnInterface } from "@/common/types";
import { useBookmarkedFiltersStore } from "@/modules/bookmarked_filters/_store";
import { useAuthenticationStore } from "@/modules/authentication/_store";
import WorkExperiencesFilter from "./WorkExperiencesFilter.vue";
import WorkExperiencesTable from "./WorkExperiencesTable.vue";
import useMixin from "@/useMixin";
import { useWorkExperiencesStore } from "../_store";
import { WorkExperienceInterface, WorkExperiencesFilterInterface } from "../types";
import { SkillInterface } from "@/modules/consultant_profiles/types";
import { apiGetSkills } from "@/modules/skills/_utils/api";
import {
  apiDeleteWorkExperience,
  apiGetWorkExperience,
  apiStoreWorkExperience,
} from "../_utils/api";
import AddWorkExperienceModal from "./AddWorkExperienceModal.vue";
import { RoleSmallInterface } from "@/modules/roles/types.js";
import { apiGetUserRoles } from "@/modules/users/_utils/api.js";

// Props
const props = defineProps({
  workExperiencesColumns: { type: Array as () => Array<TableColumnInterface>, default: undefined },
  workExperiencesFilter: {
    type: Object as () => WorkExperiencesFilterInterface,
    default: undefined,
  },
  acl: { type: Object as () => BaseAclInterface, default: undefined },
  userId: { type: Number, default: undefined },
  allowFiltering: { type: Boolean, default: false },
  allowAdd: { type: Boolean, default: false },
  title: { type: String, default: "Work Experiences" },
});

// Emits
const emits = defineEmits(["work-experience-refreshed"]);

// Pinia
const authenticationStore = useAuthenticationStore();
const bookmarkedFiltersStore = useBookmarkedFiltersStore();
const workExperiencesStore = useWorkExperiencesStore();
const workExperiencesPagination = computed<PaginationInterface | undefined>(
  () => workExperiencesStore.workExperiencesPagination
);
const profile = computed<ProfileInterface | undefined>(() => authenticationStore.profile);
const targetUserId = computed<number | undefined>(() => props.userId ?? profile.value?.id);
const skills = ref<Array<SkillInterface>>([]);
const roles = ref<Array<RoleSmallInterface>>([]);
// Mixins
const { loadTablePageSize, qsDecode, syncRouteParams, persistTablePageSize } = useMixin();

// Instances
const instance = getCurrentInstance();
const $route = instance?.proxy?.$route;
const $message = instance?.proxy?.$message;

// Data properties
const workExperiencesPage = ref<Array<WorkExperienceInterface>>([]);
const loadingTable = ref<boolean>(false);
const currentPage = ref<number>(1);
const pageSizeRef = ref<number>(25);
const filtersRef = ref<WorkExperiencesFilterInterface>({});
const sortingRef = ref<object>({});
const addWorkExperienceModalVisible = ref<boolean>(false);

// Life-cycle hooks
onBeforeMount(() => {
  bookmarkedFiltersStore.getUserBookmarkedFilters(profile.value?.id as number);
  getWorkExperiencesPagefromURL();
  apiGetSkills().then((res: any) => (skills.value = res.data.data));
  apiGetUserRoles(targetUserId.value as number).then((res: any) => (roles.value = res.data.data));
});

// Component methods
const getWorkExperiencesPagefromURL = (url: any = $route?.query): void => {
  const { page, filters, sorting, pageSize } = qsDecode(url);

  pageSizeRef.value = loadTablePageSize();

  if (page) currentPage.value = parseInt(page);
  if (filters) filtersRef.value = filters;
  if (sorting) sortingRef.value = sorting;
  if (pageSize) pageSizeRef.value = parseInt(pageSize);

  refreshWorkExperiences();
};

const getWorkExperiencesPage = ({
  page,
  pageSize = pageSizeRef.value,
  filters = filtersRef.value,
  sorting = sortingRef.value,
}: {
  page: any;
  pageSize?: number;
  filters?: WorkExperiencesFilterInterface;
  sorting?: object;
}): void => {
  loadingTable.value = true;
  currentPage.value = page;
  pageSizeRef.value = pageSize;

  if (props.allowFiltering) {
    syncRouteParams(
      {
        page: currentPage.value,
        pageSize: pageSizeRef.value,
        filters: filtersRef.value,
        sorting: sortingRef.value,
      },
      instance
    );
  }
  persistTablePageSize(pageSize);
  workExperiencesStore
    .getWorkExperiencesByPage({
      page,
      page_size: pageSize,
      ...{ ...filters, ...props.workExperiencesFilter },
      ...sorting,
    })
    .then((results: any) => {
      loadingTable.value = false;
      workExperiencesPage.value = results;
    });
};

const applyFilters = (filters: WorkExperiencesFilterInterface): void => {
  filtersRef.value = filters;
  currentPage.value = 1;
  syncRouteParams(
    {
      page: currentPage.value,
      pageSize: pageSizeRef.value,
      filters: filtersRef.value,
      sorting: sortingRef.value,
    },
    instance
  );
  refreshWorkExperiences();
};

const applySorting = (sorting: object): void => {
  sortingRef.value = sorting;

  syncRouteParams(
    {
      page: currentPage.value,
      pageSize: pageSizeRef.value,
      filters: filtersRef.value,
      sorting: sortingRef.value,
    },
    instance
  );
  refreshWorkExperiences();
};

const refreshWorkExperiences = (): void => {
  getWorkExperiencesPage({ page: currentPage.value });
};

const handleStoreWorkExperience = (newWorkExperience: WorkExperienceInterface): void => {
  loadingTable.value = true;
  apiStoreWorkExperience(newWorkExperience, targetUserId.value as number)
    .then(() => refreshWorkExperiences())
    .then(() => $message?.success("Work experience stored successfully!", 3))
    .catch(() => $message?.error("Couldn't store work experience!", 3))
    .finally(() => (loadingTable.value = false));
  addWorkExperienceModalVisible.value = false;
};

const handleUpdateWorkExperience = (updatedWorkExperience: WorkExperienceInterface): void => {
  workExperiencesStore
    .updateWorkExperience({
      workExperienceId: updatedWorkExperience.id,
      updatedWorkExperience: updatedWorkExperience,
    })
    .then(() => refreshWorkExperience(updatedWorkExperience.id))
    .then(() => $message?.success("Work experience updated successfully!", 3))
    .catch(() => $message?.error("Couldn't update work experience!", 3));
};

const handleDeleteWorkExperience = (workExperienceId: number): void => {
  apiDeleteWorkExperience(workExperienceId)
    .then(() => refreshWorkExperiences())
    .then(() => $message?.success("Work experience deleted successfully!", 3))
    .catch(() => $message?.error("Couldn't delete work experience!", 3));
};

const refreshWorkExperience = (workExperienceId: number): void => {
  apiGetWorkExperience(workExperienceId)
    .then((res) => {
      workExperiencesPage.value = workExperiencesPage.value.map((we: WorkExperienceInterface) =>
        we.id === workExperienceId ? res.data.data : we
      );
    })
    .finally(() => emits("work-experience-refreshed"));
};

defineExpose({ refreshWorkExperiences, currentPage });
</script>
